import { errorBox } from '@core/components/AlertBox';
import { useAppDispatch } from '@core/redux/hooks';
import { environment } from '@core/environment';
import { GrpcRestFetchTransport, useAuth0Interceptor } from '@deepup/api-utils';
import { iorganizationManagementClientApiV1 as iorganizationManagementClientApi } from '@deepup/internal-apis';
import { buildDefaultListProjectInfosRequest } from '@projects/organization/buildDefaultListProjectInfosRequest';
import { projectActions, projectSelectors } from '@projects/redux';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

const IDS_CHUNK = 100;

const LABELING_LOAD_FAILED =
  'Could not load project labeling types. Filters and the labeling column may be incomplete.';

const chunkIds = (ids: string[], size: number): string[][] => {
  const res: string[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    res.push(ids.slice(i, i + size));
  }
  return res;
};

/**
 * Fetches OMA labeling type for all projects in the Redux store and merges via updateMany.
 */
export const useProjectLabelingTypes = (): void => {
  const dispatch = useAppDispatch();
  const projects = useSelector(projectSelectors.selectAll);
  const authInterceptor = useAuth0Interceptor();

  const client = useMemo(
    () =>
      new iorganizationManagementClientApi.InternalOrganizationManagementServiceClient(
        new GrpcRestFetchTransport({
          baseUrl: environment.unifiedApiUrl,
          fetchInit: { credentials: 'include' },
          interceptors: [authInterceptor],
        }),
      ),
    [authInterceptor],
  );

  const idKey = useMemo(() => {
    const ids = projects.map((p) => p.id).sort();
    return ids.join(',');
  }, [projects]);

  useEffect(() => {
    const run = async () => {
      const ids = idKey ? idKey.split(',').filter(Boolean) : [];
      if (ids.length === 0) {
        return;
      }
      try {
        for (const batch of chunkIds(ids, IDS_CHUNK)) {
          const res = await client.listProjectInfos(buildDefaultListProjectInfosRequest(batch))
            .response;

          const updates = res.items.flatMap((item) => {
            const sid = item.project?.id;
            if (!sid) {
              return [];
            }
            return [
              {
                id: sid,
                changes: {
                  labelingType: item.labelingType,
                  activityState: item.activityState,
                },
              },
            ];
          });

          if (updates.length > 0) {
            dispatch(projectActions.updateMany(updates));
          }
        }
      } catch {
        errorBox(LABELING_LOAD_FAILED);
      }
    };
    void run();
  }, [client, dispatch, idKey]);
};
